import React, { useState, useMemo, useEffect } from 'react';
import {
  DocumentIcon,
  PhotoIcon,
  MusicalNoteIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  PauseIcon,
  EyeIcon,
  DocumentTextIcon,
  FilmIcon,
  ArchiveBoxIcon,
  Squares2X2Icon,
  ListBulletIcon
} from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useAllAttachments, useAttachmentStats, type AttachmentWithContext } from '../../hooks/useAllAttachments';
import type { TrackerEmail } from '../../types/tracker.types';
import './TrackerAttachmentsTab.css';

interface TrackerAttachmentsTabProps {
  emails: TrackerEmail[] | undefined;
  isLoading?: boolean;
  affaireId?: number | string;
}

export const TrackerAttachmentsTab: React.FC<TrackerAttachmentsTabProps> = ({
  emails,
  isLoading,
  affaireId: propAffaireId
}) => {
  const attachments = useAllAttachments(emails);
  const stats = useAttachmentStats(attachments);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  // Derive affaireId from emails if not passed as prop
  const affaireId = propAffaireId || emails?.find(e => e.case_number)?.case_number;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'images' | 'documents' | 'audio' | 'video' | 'other'>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Preview state
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  // Multi-select state — 2026-05-28: lets the user pick a subset of
  // documents and download only those, alongside "Tout télécharger".
  // Keys are `${emailId}-${attachmentIndex}` (same as the existing
  // thumbnails map) so we don't need a new identity scheme.
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isDownloadingSelected, setIsDownloadingSelected] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const handleUploadClick = () => fileInputRef.current?.click();
  const handleUploadFiles = async (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0 || !affaireId || isUploading) return;
    setIsUploading(true);
    try {
      const token = getAuthToken();
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:10007';
      const form = new FormData();
      Array.from(filesList).forEach((f) => form.append('documents', f));
      const response = await fetch(
        `${baseUrl}/api/admin/emails/affaire/${affaireId}/documents/upload`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
      );
      if (!response.ok) throw new Error(`Upload failed: ${response.status}`);
      // Refetch the affaire's emails so the new documents appear
      queryClient.invalidateQueries({ queryKey: ['tracker-emails'] });
    } catch (err) {
      console.error('Upload error:', err);
      alert('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const getKey = (att: AttachmentWithContext) => `${att.emailId}-${att.attachmentIndex}`;
  const toggleSelected = (att: AttachmentWithContext, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = getKey(att);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };

  // Filter and sort attachments
  const filteredAttachments = useMemo(() => {
    let filtered = attachments;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(att =>
        att.name.toLowerCase().includes(query) ||
        (att.emailSubject?.toLowerCase().includes(query) ?? false) ||
        att.senderName?.toLowerCase().includes(query)
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(att => {
        const contentType = (att.contentType || att.content_type || '').toLowerCase();
        const name = att.name.toLowerCase();

        switch (filterType) {
          case 'images':
            return contentType.startsWith('image/');
          case 'documents':
            return contentType.includes('pdf') ||
                   contentType.includes('word') ||
                   contentType.includes('document') ||
                   contentType.includes('text/') ||
                   contentType.includes('spreadsheet') ||
                   contentType.includes('excel') ||
                   name.endsWith('.pdf') ||
                   name.endsWith('.doc') ||
                   name.endsWith('.docx') ||
                   name.endsWith('.xls') ||
                   name.endsWith('.xlsx') ||
                   name.endsWith('.txt');
          case 'audio':
            return contentType.startsWith('audio/') ||
                   ['mp3', 'wav', 'ogg', 'm4a'].some(ext => name.endsWith(`.${ext}`));
          case 'video':
            return contentType.startsWith('video/') ||
                   ['mp4', 'avi', 'mov', 'webm'].some(ext => name.endsWith(`.${ext}`));
          case 'other':
            return !contentType.startsWith('image/') &&
                   !contentType.startsWith('audio/') &&
                   !contentType.startsWith('video/') &&
                   !contentType.includes('pdf') &&
                   !contentType.includes('word') &&
                   !contentType.includes('document');
          default:
            return true;
        }
      });
    }

    // Apply sorting
    const sorted = [...filtered];
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'size':
        sorted.sort((a, b) => (b.size || 0) - (a.size || 0));
        break;
      case 'date':
      default:
        sorted.sort((a, b) => new Date(b.emailDate).getTime() - new Date(a.emailDate).getTime());
        break;
    }

    return sorted;
  }, [attachments, searchQuery, filterType, sortBy]);

  const getAuthToken = (): string | null => {
    return localStorage.getItem('authToken');
  };

  const getDownloadUrl = (att: AttachmentWithContext, inline: boolean = false) => {
    const baseUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:10007'}/api/admin/emails/${att.emailId}/attachments/${att.attachmentIndex}/download`;
    return inline ? `${baseUrl}?inline=true` : baseUrl;
  };

  const fetchWithAuth = async (url: string, expectedType?: string): Promise<Blob> => {
    const token = getAuthToken();
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.status}`);
    }
    const blob = await response.blob();

    // If we have an expected type and it differs from the blob's type, recreate with correct type
    // This helps browsers properly handle PDFs in object/embed elements
    if (expectedType && blob.type !== expectedType) {
      return new Blob([blob], { type: expectedType });
    }
    return blob;
  };

  // Helper to get content type (handles both camelCase and snake_case from backend)
  const getContentType = (att: AttachmentWithContext): string => {
    return (att.contentType || att.content_type || '').toLowerCase();
  };

  const isImageFile = (contentType: string) => contentType?.startsWith('image/');
  const isPdfFile = (contentType: string, name?: string) => {
    if (contentType === 'application/pdf' || contentType?.includes('pdf')) return true;
    if (name?.toLowerCase().endsWith('.pdf')) return true;
    return false;
  };
  const isAudioFile = (contentType: string, name: string) => {
    const ext = name?.toLowerCase().split('.').pop() || '';
    return contentType?.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a'].includes(ext);
  };
  const isVideoFile = (contentType: string, name: string) => {
    const ext = name?.toLowerCase().split('.').pop() || '';
    return contentType?.startsWith('video/') || ['mp4', 'avi', 'mov', 'webm'].includes(ext);
  };

  const canPreview = (att: AttachmentWithContext) => {
    const contentType = getContentType(att);
    const name = att.name || '';
    return isImageFile(contentType) || isPdfFile(contentType, name) || isAudioFile(contentType, name) || isVideoFile(contentType, name);
  };

  const getFileIcon = (att: AttachmentWithContext) => {
    const contentType = getContentType(att);
    const name = (att.name || '').toLowerCase();

    if (contentType.startsWith('image/')) {
      return <PhotoIcon className="doc-icon doc-icon--image" />;
    }
    if (contentType.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a'].some(ext => name.endsWith(`.${ext}`))) {
      return <MusicalNoteIcon className="doc-icon doc-icon--audio" />;
    }
    if (contentType.startsWith('video/') || ['mp4', 'avi', 'mov', 'webm'].some(ext => name.endsWith(`.${ext}`))) {
      return <FilmIcon className="doc-icon doc-icon--video" />;
    }
    if (contentType.includes('pdf')) {
      return <DocumentTextIcon className="doc-icon doc-icon--pdf" />;
    }
    if (contentType.includes('zip') || contentType.includes('rar') || contentType.includes('archive')) {
      return <ArchiveBoxIcon className="doc-icon doc-icon--archive" />;
    }
    return <DocumentIcon className="doc-icon doc-icon--document" />;
  };

  const getFileTypeLabel = (att: AttachmentWithContext): string => {
    const ext = att.name.split('.').pop()?.toUpperCase() || '';
    return ext;
  };

  const formatFileSize = (bytes: number | undefined): string => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load thumbnails for images
  useEffect(() => {
    const loadThumbnails = async () => {
      const newThumbnails: Record<string, string> = {};

      for (const att of filteredAttachments) {
        const key = `${att.emailId}-${att.attachmentIndex}`;
        if (thumbnails[key]) continue; // Already loaded
        if (!isImageFile(getContentType(att))) continue; // Only images

        try {
          const url = getDownloadUrl(att, true);
          const blob = await fetchWithAuth(url);
          const blobUrl = URL.createObjectURL(blob);
          newThumbnails[key] = blobUrl;
        } catch (error) {
          console.error('Failed to load thumbnail:', error);
        }
      }

      if (Object.keys(newThumbnails).length > 0) {
        setThumbnails(prev => ({ ...prev, ...newThumbnails }));
      }
    };

    // Load thumbnails for visible images (limit to first 20 for performance)
    const imagesToLoad = filteredAttachments
      .filter(att => isImageFile(getContentType(att)))
      .slice(0, 20);

    if (imagesToLoad.length > 0) {
      loadThumbnails();
    }
  }, [filteredAttachments]);

  // Cleanup thumbnails on unmount
  useEffect(() => {
    return () => {
      Object.values(thumbnails).forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const loadPreview = async (index: number) => {
    const att = filteredAttachments[index];
    if (!att) return;

    setPreviewLoading(true);
    setPreviewIndex(index);

    // Stop any playing audio
    if (audioElement) {
      audioElement.pause();
      setAudioElement(null);
      setIsPlaying(false);
    }

    try {
      const url = getDownloadUrl(att, true);
      const contentType = getContentType(att);

      // Determine expected MIME type for PDF files to ensure proper rendering
      let expectedType: string | undefined;
      if (isPdfFile(contentType, att.name)) {
        expectedType = 'application/pdf';
      }

      const blob = await fetchWithAuth(url, expectedType);
      const blobUrl = URL.createObjectURL(blob);

      // Clean up previous URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(blobUrl);

      // If audio, create audio element
      if (isAudioFile(contentType, att.name)) {
        const audio = new Audio(blobUrl);
        audio.addEventListener('ended', () => setIsPlaying(false));
        setAudioElement(audio);
      }
    } catch (error) {
      console.error('Preview error:', error);
      alert('Erreur lors du chargement de l\'aperçu');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    if (audioElement) {
      audioElement.pause();
    }
    setPreviewIndex(null);
    setPreviewUrl(null);
    setAudioElement(null);
    setIsPlaying(false);
  };

  const goToPrevious = () => {
    if (previewIndex === null || previewIndex <= 0) return;
    loadPreview(previewIndex - 1);
  };

  const goToNext = () => {
    if (previewIndex === null || previewIndex >= filteredAttachments.length - 1) return;
    loadPreview(previewIndex + 1);
  };

  const handleDownload = async (att: AttachmentWithContext) => {
    try {
      const url = getDownloadUrl(att, false);
      const blob = await fetchWithAuth(url);
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = att.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      alert('Erreur lors du téléchargement du fichier');
    }
  };

  const handleDownloadSelected = async () => {
    if (selectedKeys.size === 0 || isDownloadingSelected) return;
    const items = filteredAttachments.filter((a) => selectedKeys.has(getKey(a)));
    if (items.length === 0) return;
    setIsDownloadingSelected(true);
    try {
      // No bulk endpoint for a custom set — fetch each file individually
      // and trigger one download per item. Modern browsers handle small
      // sequential downloads fine; for many items (>20) we'd want a zip
      // endpoint, deferred until needed.
      for (const att of items) {
        // eslint-disable-next-line no-await-in-loop
        await handleDownload(att);
      }
      setSelectedKeys(new Set());
    } finally {
      setIsDownloadingSelected(false);
    }
  };

  const handleDownloadAll = async () => {
    if (!affaireId || isDownloadingAll) return;
    setIsDownloadingAll(true);
    try {
      const token = getAuthToken();
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:10007';
      const response = await fetch(`${baseUrl}/api/admin/emails/affaire/${affaireId}/attachments/download-all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Failed: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `affaire_${affaireId}_documents.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download all error:', error);
      alert('Erreur lors du téléchargement des documents');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  const toggleAudio = () => {
    if (!audioElement) return;
    if (isPlaying) {
      audioElement.pause();
      setIsPlaying(false);
    } else {
      audioElement.play();
      setIsPlaying(true);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (previewIndex === null) return;
      if (e.key === 'Escape') closePreview();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewIndex, filteredAttachments.length]);

  const currentPreviewAttachment = previewIndex !== null ? filteredAttachments[previewIndex] : null;

  if (isLoading) {
    return (
      <div className="docs-tab">
        <div className="docs-tab__loading">
          <div className="docs-loading-spinner"></div>
          <span>Chargement des documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="docs-tab">
      {/* Header with stats */}
      <div className="docs-tab__header">
        <div className="docs-tab__stats">
          <div className="docs-stat">
            <span className="docs-stat__value">{stats.totalCount}</span>
            <span className="docs-stat__label">Documents</span>
          </div>
          <div className="docs-stat">
            <span className="docs-stat__value">{formatFileSize(stats.totalSize)}</span>
            <span className="docs-stat__label">Total</span>
          </div>
        </div>

        <div className="docs-tab__header-actions">
          {affaireId && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => handleUploadFiles(e.target.files)}
              />
              <button
                className="docs-upload-btn"
                onClick={handleUploadClick}
                disabled={isUploading}
                title="Ajouter un ou plusieurs documents au dossier"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                <span>{isUploading ? 'Upload…' : 'Ajouter un document'}</span>
              </button>
            </>
          )}
          {selectedKeys.size > 0 && (
            <>
              <button
                className="docs-download-selected-btn"
                onClick={handleDownloadSelected}
                disabled={isDownloadingSelected}
                title={`Télécharger les ${selectedKeys.size} document${selectedKeys.size > 1 ? 's' : ''} sélectionné${selectedKeys.size > 1 ? 's' : ''}`}
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                <span>
                  {isDownloadingSelected
                    ? 'Téléchargement...'
                    : `Télécharger la sélection (${selectedKeys.size})`}
                </span>
              </button>
              <button
                className="docs-clear-selection-btn"
                onClick={() => setSelectedKeys(new Set())}
                title="Effacer la sélection"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </>
          )}
          {affaireId && stats.totalCount > 0 && (
            <button
              className="docs-download-all-btn"
              onClick={handleDownloadAll}
              disabled={isDownloadingAll}
              title={`Télécharger tous les documents (${stats.totalCount} fichiers)`}
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>{isDownloadingAll ? 'Téléchargement...' : 'Tout télécharger'}</span>
            </button>
          )}
          <div className="docs-tab__view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vue grille"
            >
              <Squares2X2Icon className="w-5 h-5" />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vue liste"
            >
              <ListBulletIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="docs-tab__filters">
        <div className="docs-search">
          <MagnifyingGlassIcon className="docs-search__icon" />
          <input
            type="text"
            placeholder="Rechercher un document..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="docs-search__input"
          />
        </div>

        <div className="docs-filter-group">
          <FunnelIcon className="docs-filter-icon" />
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="docs-filter-select"
          >
            <option value="all">Tous les types</option>
            <option value="documents">Documents</option>
            <option value="images">Images</option>
            <option value="audio">Audio</option>
            <option value="video">Vidéo</option>
            <option value="other">Autres</option>
          </select>
        </div>

        <div className="docs-filter-group">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="docs-filter-select"
          >
            <option value="date">Plus récent</option>
            <option value="name">Nom A-Z</option>
            <option value="size">Taille</option>
          </select>
        </div>
      </div>

      {/* Documents grid/list */}
      {filteredAttachments.length === 0 ? (
        <div className="docs-tab__empty">
          <DocumentIcon className="docs-empty-icon" />
          <h3>Aucun document</h3>
          <p>
            {searchQuery || filterType !== 'all'
              ? 'Aucun document ne correspond à votre recherche'
              : 'Aucun document trouvé dans les emails'}
          </p>
        </div>
      ) : (
        <div className={`docs-tab__content ${viewMode === 'grid' ? 'docs-grid' : 'docs-list'}`}>
          {filteredAttachments.map((att, index) => {
            const key = `${att.emailId}-${att.attachmentIndex}`;
            const thumbnail = thumbnails[key];
            const contentType = getContentType(att);
            const isImage = isImageFile(contentType);
            const previewable = canPreview(att);

            const isSelected = selectedKeys.has(key);
            return viewMode === 'grid' ? (
              <div
                key={`${key}-${index}`}
                className={`doc-card ${previewable ? 'doc-card--clickable' : ''} ${isSelected ? 'doc-card--selected' : ''}`}
                onClick={() => previewable ? loadPreview(index) : handleDownload(att)}
              >
                <label
                  className="doc-card__select"
                  onClick={(e) => e.stopPropagation()}
                  title={isSelected ? 'Désélectionner' : 'Sélectionner'}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => toggleSelected(att, e as any)}
                  />
                </label>
                <div className="doc-card__preview">
                  {isImage && thumbnail ? (
                    <img src={thumbnail} alt={att.name} className="doc-card__thumbnail" />
                  ) : (
                    <div className="doc-card__icon-wrapper">
                      {getFileIcon(att)}
                    </div>
                  )}
                  <div className="doc-card__type-badge">{getFileTypeLabel(att)}</div>
                  <div className="doc-card__overlay">
                    {previewable ? <EyeIcon className="w-8 h-8" /> : <ArrowDownTrayIcon className="w-8 h-8" />}
                    <span>{previewable ? 'Aperçu' : 'Télécharger'}</span>
                  </div>
                </div>
                <div className="doc-card__info">
                  <div className="doc-card__name" title={att.name}>{att.name}</div>
                  <div className="doc-card__meta">
                    <span>{formatFileSize(att.size)}</span>
                    <span className="doc-card__dot">•</span>
                    <span>{formatDate(att.emailDate)}</span>
                  </div>
                </div>
                <div className="doc-card__actions">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                    className="doc-card__download-btn"
                    title="Télécharger"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ) : (
              <div
                key={`${key}-${index}`}
                className={`doc-row ${isSelected ? 'doc-row--selected' : ''}`}
                onClick={() => previewable ? loadPreview(index) : handleDownload(att)}
              >
                <label
                  className="doc-row__select"
                  onClick={(e) => e.stopPropagation()}
                  title={isSelected ? 'Désélectionner' : 'Sélectionner'}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => toggleSelected(att, e as any)}
                  />
                </label>
                <div className="doc-row__preview">
                  {isImage && thumbnail ? (
                    <img src={thumbnail} alt={att.name} className="doc-row__thumbnail" />
                  ) : (
                    <div className="doc-row__icon-wrapper">
                      {getFileIcon(att)}
                    </div>
                  )}
                </div>
                <div className="doc-row__info">
                  <div className="doc-row__name">{att.name}</div>
                  <div className="doc-row__email">
                    <span className="doc-row__email-label">De:</span>
                    <span className="doc-row__email-subject">{att.emailSubject}</span>
                    {att.senderName && (
                      <>
                        <span className="doc-row__email-separator">-</span>
                        <span className="doc-row__email-sender">{att.senderName}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="doc-row__meta">
                  <span className="doc-row__size">{formatFileSize(att.size)}</span>
                  <span className="doc-row__date">{formatDateTime(att.emailDate)}</span>
                </div>
                <div className="doc-row__actions">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(att); }}
                    className="doc-row__action-btn doc-row__action-btn--primary"
                    title="Télécharger"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview Modal - Lightbox Style */}
      {previewIndex !== null && currentPreviewAttachment && (
        <div className="doc-preview-overlay" onClick={closePreview}>
          {/* Header Bar */}
          <div className="doc-preview-header" onClick={(e) => e.stopPropagation()}>
            <div className="doc-preview-header__info">
              <span className="doc-preview-header__filename">{currentPreviewAttachment.name}</span>
              <span className="doc-preview-header__meta">
                {formatFileSize(currentPreviewAttachment.size)} • {previewIndex + 1} / {filteredAttachments.length}
              </span>
            </div>
            <div className="doc-preview-header__actions">
              <button
                onClick={() => handleDownload(currentPreviewAttachment)}
                className="doc-preview-header__btn"
                title="Télécharger"
              >
                <ArrowDownTrayIcon className="w-5 h-5" />
              </button>
              <button
                onClick={closePreview}
                className="doc-preview-header__btn doc-preview-header__btn--close"
                title="Fermer (Échap)"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="doc-preview-content" onClick={(e) => e.stopPropagation()}>
            {previewLoading ? (
              <div className="doc-preview-loading">
                <div className="docs-loading-spinner docs-loading-spinner--light"></div>
                <span>Chargement...</span>
              </div>
            ) : previewUrl && (() => {
              const previewContentType = getContentType(currentPreviewAttachment);
              return (
                <>
                  {isImageFile(previewContentType) && (
                    <img
                      src={previewUrl}
                      alt={currentPreviewAttachment.name}
                      className="doc-preview-image"
                    />
                  )}
                  {isPdfFile(previewContentType, currentPreviewAttachment.name) && (
                    <object
                      data={`${previewUrl}#toolbar=1&navpanes=0&scrollbar=1`}
                      type="application/pdf"
                      className="doc-preview-pdf"
                      title={currentPreviewAttachment.name}
                    >
                      <div className="doc-preview-pdf-fallback">
                        <DocumentTextIcon className="w-16 h-16" />
                        <p>Impossible d'afficher le PDF dans le navigateur</p>
                        <button
                          onClick={() => handleDownload(currentPreviewAttachment)}
                          className="doc-preview-pdf-download-btn"
                        >
                          <ArrowDownTrayIcon className="w-5 h-5" />
                          Télécharger le PDF
                        </button>
                      </div>
                    </object>
                  )}
                  {isVideoFile(previewContentType, currentPreviewAttachment.name) && (
                    <video
                      src={previewUrl}
                      controls
                      autoPlay
                      className="doc-preview-video"
                    />
                  )}
                  {isAudioFile(previewContentType, currentPreviewAttachment.name) && (
                    <div className="doc-preview-audio">
                      <div className="doc-preview-audio__player">
                        <button onClick={toggleAudio} className="doc-preview-audio__btn">
                          {isPlaying ? (
                            <PauseIcon className="w-16 h-16" />
                          ) : (
                            <PlayIcon className="w-16 h-16" />
                          )}
                        </button>
                        <div className="doc-preview-audio__info">
                          <span className="doc-preview-audio__name">{currentPreviewAttachment.name}</span>
                          <span className="doc-preview-audio__size">{formatFileSize(currentPreviewAttachment.size)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Floating Navigation Buttons */}
          <button
            onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
            disabled={previewIndex === 0}
            className={`doc-preview-nav doc-preview-nav--prev ${previewIndex === 0 ? 'disabled' : ''}`}
            title="Précédent (←)"
          >
            <ChevronLeftIcon className="w-8 h-8" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            disabled={previewIndex === filteredAttachments.length - 1}
            className={`doc-preview-nav doc-preview-nav--next ${previewIndex === filteredAttachments.length - 1 ? 'disabled' : ''}`}
            title="Suivant (→)"
          >
            <ChevronRightIcon className="w-8 h-8" />
          </button>
        </div>
      )}
    </div>
  );
};
